import React from "react";
import { Box, Typography, Button, Paper, Stack, Fade } from "@mui/material";
import { Inbox } from "@mui/icons-material";
import { flexCenter, flexColumn } from "../../theme/responsive";
import { ICON_SIZES } from "../../theme/constants";

/**
 * Reusable EmptyState component for when there's no data
 * @param {string} title - Main message
 * @param {string} description - Additional description
 * @param {object} icon - Icon component to display
 * @param {object} action - Action button {label, onClick, icon, variant, color}
 * @param {object} secondaryAction - Secondary action button
 * @param {boolean} elevated - Whether to show elevated paper background
 * @param {string} minHeight - Minimum height of empty state
 * @param {object} sx - Additional MUI sx props
 */
export default function EmptyState({
  title = "No data available",
  description,
  icon = <Inbox />,
  action,
  secondaryAction,
  elevated = false,
  minHeight,
  sx = {},
}) {
  const content = (
    <Box
      sx={{
        ...flexCenter,
        ...flexColumn,
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 3 },
        textAlign: "center",
        minHeight: minHeight || "auto",
        width: "100%",
        ...sx,
      }}
    >
      <Fade in timeout={500}>
        <Box>
          <Box
            sx={{
              width: { xs: 72, md: 80 },
              height: { xs: 72, md: 80 },
              borderRadius: "50%",
              bgcolor: "grey.100",
              ...flexCenter,
              mb: 3,
              mx: "auto",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                transform: "scale(1.05)",
                bgcolor: "grey.200",
              },
            }}
          >
            {React.cloneElement(icon, {
              sx: {
                fontSize: { xs: ICON_SIZES.xl, md: ICON_SIZES.xxl },
                color: "text.secondary",
                transition: "color 0.3s ease-in-out",
              },
            })}
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: description ? 1 : 2,
              fontSize: { xs: "1rem", md: "1.25rem" },
            }}
          >
            {title}
          </Typography>

          {description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 3,
                maxWidth: { xs: "100%", md: 400 },
                fontSize: { xs: "0.875rem", md: "1rem" },
                lineHeight: 1.6,
              }}
            >
              {description}
            </Typography>
          )}

          {(action || secondaryAction) && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
              sx={{ mt: 2, width: { xs: "100%", sm: "auto" } }}
            >
              {action && (
                <Button
                  variant={action.variant || "contained"}
                  color={action.color || "primary"}
                  size={action.size || "medium"}
                  startIcon={action.icon}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  fullWidth={action.fullWidth}
                >
                  {action.label}
                </Button>
              )}
              {secondaryAction && (
                <Button
                  variant={secondaryAction.variant || "outlined"}
                  color={secondaryAction.color || "primary"}
                  size={secondaryAction.size || "medium"}
                  startIcon={secondaryAction.icon}
                  onClick={secondaryAction.onClick}
                  disabled={secondaryAction.disabled}
                  fullWidth={secondaryAction.fullWidth}
                >
                  {secondaryAction.label}
                </Button>
              )}
            </Stack>
          )}
        </Box>
      </Fade>
    </Box>
  );

  if (elevated) {
    return (
      <Paper
        elevation={1}
        sx={{
          borderRadius: 2,
          ...sx,
        }}
      >
        {content}
      </Paper>
    );
  }

  return content;
}
